import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, Loader2, MessageCircle, Share2, Unlink } from "lucide-react";

import { Modal } from "../modal";

export type ShareLinkStatus = "loading" | "ready" | "failed";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    subtitle: string;
    status: ShareLinkStatus;
    url: string | null;
    /**
     * What goes with the link on WhatsApp and the phone's share sheet. Written
     * by each tool, so the patient is told what they are opening.
     */
    message: string;
    onRetry?: () => void;
    /** Whether somebody is on the link, in words, where the tool knows. */
    presence?: string | null;
    /** A line under everything else: how long the link lasts, who it reaches. */
    footnote?: ReactNode;
    /** Ends the link for everybody holding it. */
    onRevoke?: () => void;
};

/**
 * One way to hand a patient a link, for every tool that has one.
 *
 * The link on screen to select by hand, because a clipboard can refuse; copy;
 * WhatsApp with a message already written, since that is how most therapists
 * already talk to their patients; and the phone's own share sheet where there
 * is one. Each tool says what the link opens and, where it knows, whether
 * anybody has opened it.
 */
export default function ShareLinkDialog({
    isOpen,
    onClose,
    title,
    subtitle,
    status,
    url,
    message,
    onRetry,
    presence,
    footnote,
    onRevoke,
}: Props) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

    useEffect(() => {
        if (!copied) return;
        const id = window.setTimeout(() => setCopied(false), 2000);
        return () => window.clearTimeout(id);
    }, [copied]);

    const copy = async () => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
        } catch {
            // The link is on screen to select by hand.
        }
    };

    const nativeShare = () => {
        if (!url) return;
        void navigator.share({ title, text: message, url }).catch(() => undefined);
    };

    const action =
        "flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800/50";

    return (
        <Modal isOpen={isOpen} onClose={onClose} showCloseButton={false} className="mx-4 max-w-lg rounded-3xl">
            <div className="flex max-h-[80vh] flex-col overflow-y-auto p-5" data-testid="share-link-dialog">
                <h2 className="text-lg font-bold text-gray-800 dark:text-white">{title}</h2>
                <p className="mb-4 text-sm text-content-tertiary">{subtitle}</p>

                {status === "loading" && (
                    <p role="status" className="mb-4 flex items-center gap-2 text-sm text-content-tertiary">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        {t("shareLink.preparing")}
                    </p>
                )}

                {status === "failed" && (
                    <div className="mb-4">
                        <p role="alert" className="mb-2 text-sm text-red-600">
                            {t("shareLink.failed")}
                        </p>
                        {onRetry && (
                            <button type="button" onClick={onRetry} className={action}>
                                {t("shareLink.retry")}
                            </button>
                        )}
                    </div>
                )}

                {status === "ready" && url && (
                    <>
                        <label htmlFor="share-link-url" className="mb-1 text-xs font-semibold text-content-tertiary">
                            {t("shareLink.linkLabel")}
                        </label>
                        <input
                            id="share-link-url"
                            readOnly
                            value={url}
                            onFocus={(event) => event.currentTarget.select()}
                            className="mb-3 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                        />
                        <div className="mb-4 flex flex-wrap gap-2">
                            <button type="button" onClick={() => void copy()} className={action}>
                                {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
                                {copied ? t("shareLink.copied") : t("shareLink.copy")}
                            </button>
                            <a
                                href={`https://wa.me/?text=${encodeURIComponent(message)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={action}
                            >
                                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                                {t("shareLink.whatsapp")}
                            </a>
                            {canNativeShare && (
                                <button type="button" onClick={nativeShare} className={action}>
                                    <Share2 className="h-4 w-4" aria-hidden="true" />
                                    {t("shareLink.more")}
                                </button>
                            )}
                        </div>
                        {presence && (
                            <p role="status" className="mb-1 text-sm font-medium text-content-secondary" data-testid="share-link-presence">
                                {presence}
                            </p>
                        )}
                        {footnote && <p className="text-xs text-content-tertiary">{footnote}</p>}
                        {onRevoke && (
                            <button
                                type="button"
                                onClick={onRevoke}
                                className="mt-4 flex min-h-11 items-center justify-center gap-2 rounded-xl border border-red-200 px-3 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 dark:border-red-500/30 dark:hover:bg-red-500/10"
                            >
                                <Unlink className="h-4 w-4" aria-hidden="true" />
                                {t("shareLink.revoke")}
                            </button>
                        )}
                    </>
                )}

                <button
                    type="button"
                    onClick={onClose}
                    className="mt-4 w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800/50"
                >
                    {t("common.close")}
                </button>
            </div>
        </Modal>
    );
}
